/**
 * Freezing the **effect-bearing entity ids** a combatant carries into the fight — the pilot's
 * own active-effect sources, pilot gear, and the frame trait / core system effect entries the
 * overlay resolves against.
 */

import { CORE_POWER_POOL, type AffordanceLimited, type AffordanceSource, type PilotData } from "@lancer-vtt/domain";
import { parseActionFrequency, type FrozenEconomyFacts } from "./actionFreeze";

/**
 * Freeze the pilot's **active-effect-bearing entity ids** for combat
 * (`CombatantBase.pilotEffectIds`). Snapshots, at spawn, the overlay entity ids the
 * resolver gathers a pilot's *active/triggered* effects from:
 *
 *   - **talent ranks** — for each owned talent at chosen rank N, the **cumulative** ranks
 *     1..N (COMP/CON's `UnlockedRanks`), each as the overlay id `${talentId}:${rank}` (1-based).
 *     This mirrors the cumulative-rank handling of `compute.ts`'s `collectPilotBonuses` — it
 *     freezes *every* unlocked rank, not just the top one — so a rank-1 talent effect still
 *     fires for a rank-N pilot.
 *   - **core bonuses** — each taken `core_bonus` id (`cb_…`) verbatim (a core bonus is a
 *     rankless pick; its id IS the overlay key, matching the `AUTO_STABILIZING_HARDPOINTS`
 *     seed entry).
 *
 * **No compendium needed** (unlike `collectPilotBonuses`, which reads each entity's `bonuses`
 * array): this only produces *ids*, and the stored ranks are already reducer-validated at
 * `set_talent` time (`rank ≤ talent.ranks.length`). Keeping it pure over `PilotData` means the
 * freeze does not depend on whichever compendium the spawn driver happens to hold — a talent
 * from a world pack the deploy driver didn't load still freezes its id (a missing overlay
 * entry is an honest no-op at gather time, never a crash). The expansion happens **once,
 * here** — combat reads the frozen list and never re-derives it from the source pilot. An empty
 * result (`[]`) ⇒ the pilot carries no talents/core bonuses; the caller omits the field rather
 * than freezing an empty array.
 *
 * **Skill triggers are deliberately excluded:** a trigger grants only flat dice to *trigger
 * rolls*, never a combat-active effect (`PilotData.skills` — "Skills carry no `bonuses`"), so
 * there is nothing combat-relevant to freeze.
 */
export function freezePilotEffectIds(pilot: PilotData): string[] {
  const ids: string[] = [];
  for (const [talentId, rank] of Object.entries(pilot.talents)) {
    for (let r = 1; r <= rank; r++) ids.push(`${talentId}:${r}`);
  }
  for (const bonusId of pilot.coreBonuses) ids.push(bonusId);
  return ids;
}

/**
 * Freeze the pilot's **gear-effect-bearing entity ids** for combat
 * (`CombatantBase.pilotGearEffectIds`) — the pilot-gear gather vector's engine half. Snapshots, at
 * spawn, the overlay entity ids the resolver gathers a pilot's *active/triggered* gear effects
 * from: `pilot.gear`'s ids, verbatim.
 *
 * **A pilot-gear id is a native compendium id already** (`pg_…`, like `pg_mobility_hardsuit`) —
 * unlike a frame trait (which carries no native id and needs slug synthesis), so this is a
 * straight pass-through, mirroring {@link freezePilotEffectIds}'s core-bonus arm exactly. No
 * compendium lookup needed: every taken gear id freezes, whether or not it carries a DSL clause
 * (an unauthored/non-DSL item — most gear — is an honest no-op at gather time, matching the
 * core-bonus convention). `pilot.gear` is one flat id list spanning all three RAW categories
 * (weapon/armor/gear, `pilotGear.ts`'s per-category split is derived off the compendium at gate
 * time, never stored) — this freeze doesn't care which category an id belongs to, only whether
 * an overlay entry exists for it.
 *
 * **Called from `spawnPilot` and (via {@link freezePilotBlock}) `spawnDismountedPilot` — NEVER
 * `spawnMech`.** Pilot gear belongs to the pilot:
 * RAW gives no fielded pilot-gear item an effect that acts on the mech the pilot happens to be
 * piloting, and the resolver has no way to scope a mech-frozen copy to "only when unmounted" —
 * `PILOT_EFFECT_VECTOR.walk` (`effects/loader.ts`) takes no `state` and so cannot read which
 * combatant kind it's walking. Moving the freeze to the pilot-shaped carriers is therefore the
 * fix, not a gather-time filter.
 */
export function freezePilotGearEffectIds(pilot: PilotData): string[] {
  return [...pilot.gear];
}

/**
 * Lowercase snake_case slug: every run of non-`[a-z0-9]` characters collapses to one `_`,
 * trimmed. Distinct from `effects/sidecar.ts`'s `slugifyEntityId` (hyphenated, shapes a
 * FILENAME for an already-canonical id) — this one SYNTHESIZES the overlay entry id itself
 * (`"Everest"` → `"everest"`, `"Replaceable parts"` → `"replaceable_parts"`), matching the
 * `t_talent_id`/`cb_core_bonus` snake_case convention every other overlay id already uses.
 */
export function slugifySnake(s: string): string {
  return s
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "");
}

/**
 * The minimal shape a frame trait entry carries: its display `name` — traits carry no native
 * id — plus its own `active_effects[]`, each optionally
 * naming a RAW `frequency` string (the Everest's Initiative: `"1/scene"`) that {@link
 * freezeFrameTraitCaps} reads. A trait with no `active_effects` (most traits — a passive
 * stat trait like Replaceable Parts) carries no frequency; that's the common case.
 */
type RawFrameTrait = { name?: unknown; description?: unknown; active_effects?: { frequency?: unknown }[] };

/**
 * One frame trait's synthesized overlay id + display name + (optional) RAW frequency string —
 * the shared derivation {@link freezeFrameTraitEffectIds} (ids only) and {@link
 * freezeFrameTraitCaps} (frequency → usage cap) both build on, so the two freezes can never
 * drift on which trait a given id names.
 *
 * **Frame traits carry no native compendium id** (unlike a talent rank or a core bonus) — RAW
 * data (`frames.json`) only supplies each trait's display `name` (e.g. the Everest's
 * "Initiative", "Replaceable parts"). The id is therefore SYNTHESIZED here:
 * `${slugifySnake(frame.name)}_${slugifySnake(trait.name)}`. For the Everest this produces
 * `everest_initiative` / `everest_replaceable_parts` — the ids the Everest pack entries assume.
 *
 * **Pure over the already-resolved frame** (like {@link frameIntegratedWeaponIds}, unlike the
 * pure-over-`PilotData` {@link freezePilotEffectIds}): the caller already looked the frame up
 * once (for armor/structure); this only derives ids off that resolved object — no second
 * compendium read.
 *
 * An unnamed trait entry (malformed data — never observed in core/Long Rim data) is skipped, an
 * honest no-op, mirroring every other freeze's degrade-on-missing-field convention. A frame with
 * no `traits[]` (or no frame/frame name at all) returns `[]`.
 */
export function frameTraitEffectEntries(
  frame: Record<string, unknown> | undefined,
): { id: string; name: string; description?: string; frequency?: string }[] {
  if (!frame) return [];
  const frameName = typeof frame["name"] === "string" ? frame["name"] : undefined;
  if (!frameName) return [];
  const traits = frame["traits"];
  if (!Array.isArray(traits)) return [];
  const frameSlug = slugifySnake(frameName);
  const out: { id: string; name: string; description?: string; frequency?: string }[] = [];
  for (const t of traits as RawFrameTrait[]) {
    if (!t || typeof t.name !== "string" || !t.name.trim()) continue;
    // A trait's OWN cadence lives on its `active_effects[]` (RAW frames.json), not the trait
    // object itself — take the first named frequency among them (every core/Long Rim trait
    // with `active_effects` carries exactly one entry; a future multi-effect trait would need
    // per-effect ids, out of scope for this single-cadence convention).
    const activeEffects = t.active_effects;
    const frequency = Array.isArray(activeEffects)
      ? activeEffects.map((e) => (e && typeof e.frequency === "string" ? e.frequency : undefined)).find((f) => f !== undefined)
      : undefined;
    out.push({
      id: `${frameSlug}_${slugifySnake(t.name)}`,
      name: t.name,
      ...(typeof t.description === "string" ? { description: t.description } : {}),
      ...(frequency !== undefined ? { frequency } : {}),
    });
  }
  return out;
}

/**
 * The minimal shape a frame's `core_system` carries (the Everest's Hyperspec Fuel Injector /
 * "Power Up"): its own `name` (the system's GMS
 * designation) + `active_name` (the RAW-printed activation name, e.g. "Power Up" — falls
 * back to `name` when absent) + `active_effect` (the activation's prose) + `frequency`
 * (the activation's OWN cadence, e.g. `"1/scene"` — a top-level field, unlike a trait's
 * nested `active_effects[].frequency`) + `active_actions[]` (further actions the core
 * system GRANTS once active — e.g. the Everest's free 1/turn Boost — each carrying its
 * own `name`/`detail`/`trigger` cadence string, same `N/grain` shape `frequency` uses).
 */
type RawCoreSystem = {
  name?: unknown;
  active_name?: unknown;
  active_effect?: unknown;
  frequency?: unknown;
  active_actions?: { name?: unknown; detail?: unknown; trigger?: unknown }[];
};

/**
 * A frame's `core_system`, synthesized into the SAME `{id, name, description?, frequency?}`
 * shape {@link frameTraitEffectEntries} produces for `traits[]` — `core_system` is a distinct
 * top-level frame field, not a `traits[]` entry, so the frame-trait freeze doesn't cover it. Two
 * kinds of entry come out, both id-synthesized off the frame + the core system (no native
 * compendium id, exactly like a trait):
 *
 *   - **the core system's own activation** — one entry, id `${frameSlug}_${slugifySnake(cs.name)}`
 *     (for the Everest: `everest_hyperspec_fuel_injector`), display name `cs.active_name`
 *     (falling back to `cs.name`), description `cs.active_effect`, frequency `cs.frequency`
 *     (display only: it always freezes the {@link CORE_POWER_LIMITED} pool, see {@link freezeFrameTraitCaps}).
 *   - **each of the core system's OWN granted `active_actions[]`** (the Everest's free Boost) —
 *     one entry per action, id `${frameSlug}_${slugifySnake(cs.name)}_${slugifySnake(actionSlugName)}`
 *     where `actionSlugName` strips a trailing parenthetical off the action's RAW name first (the
 *     Everest's own data redundantly names it "Boost (Hyperspec Fuel Injector)" — stripping the
 *     parenthetical yields the clean `everest_hyperspec_fuel_injector_boost`, and the strip is a
 *     no-op for a core system whose active-action name carries no parenthetical, e.g. the
 *     Blackbeard's "Omni-harpoon"), display name the RAW action name verbatim (parenthetical kept
 *     for display), description `detail`, frequency `trigger` (same `N/grain` string shape
 *     `parseActionFrequency` already parses — RAW just names the field differently here).
 *
 * A frame with no `core_system` (or an unnamed one — malformed data, never observed) returns `[]`.
 */
function coreSystemEffectEntries(
  frame: Record<string, unknown> | undefined,
): { id: string; name: string; description?: string; frequency?: string }[] {
  if (!frame) return [];
  const frameName = typeof frame["name"] === "string" ? frame["name"] : undefined;
  if (!frameName) return [];
  const cs = frame["core_system"];
  if (!cs || typeof cs !== "object") return [];
  const raw = cs as RawCoreSystem;
  if (typeof raw.name !== "string" || !raw.name.trim()) return [];
  const frameSlug = slugifySnake(frameName);
  const csSlug = slugifySnake(raw.name);
  const out: { id: string; name: string; description?: string; frequency?: string }[] = [];
  out.push({
    id: `${frameSlug}_${csSlug}`,
    name: typeof raw.active_name === "string" && raw.active_name.trim() ? raw.active_name : raw.name,
    ...(typeof raw.active_effect === "string" ? { description: raw.active_effect } : {}),
    ...(typeof raw.frequency === "string" ? { frequency: raw.frequency } : {}),
  });
  for (const a of raw.active_actions ?? []) {
    if (!a || typeof a.name !== "string" || !a.name.trim()) continue;
    const slugName = a.name.replace(/\s*\([^)]*\)\s*$/, "").trim() || a.name;
    out.push({
      id: `${frameSlug}_${csSlug}_${slugifySnake(slugName)}`,
      name: a.name,
      ...(typeof a.detail === "string" ? { description: a.detail } : {}),
      ...(typeof a.trigger === "string" ? { frequency: a.trigger } : {}),
    });
  }
  return out;
}

/**
 * A frame's full set of trait- AND core-system-derived activatable entries — {@link
 * frameTraitEffectEntries} (`traits[]`) unioned with {@link coreSystemEffectEntries}
 * (`core_system`), the two RAW sources a frame's own
 * (non-loadout-granted) active content can come from. Every downstream consumer of "a frame's
 * own activatable ids" ({@link freezeFrameTraitEffectIds}, {@link freezeFrameTraitCaps},
 * `actionRegistry.ts`'s `frameEffectDisplay`, `sidecar.ts`'s `resolvesAsFrameTrait`) reads THIS
 * union, not either source alone — so a core-system id is recognized everywhere a trait id is,
 * through the exact same call sites (no new gather/enumerate/gate).
 */
export function frameEffectEntries(
  frame: Record<string, unknown> | undefined,
): { id: string; name: string; description?: string; frequency?: string }[] {
  return [...frameTraitEffectEntries(frame), ...coreSystemEffectEntries(frame)];
}

/**
 * Freeze the frame's **trait- and core-system-effect-bearing entity ids** for combat
 * (`CombatantBase.frameEffectIds`). Mirrors {@link
 * freezePilotEffectIds} precisely: a flat list of overlay entity ids the resolver gathers a
 * combatant's frame-trait/core-system *active/triggered* effects and conditional modifiers
 * from, frozen once at spawn (never re-derived from the compendium in combat). See {@link
 * frameEffectEntries} for the id-synthesis convention this reduces to.
 */
export function freezeFrameTraitEffectIds(frame: Record<string, unknown> | undefined): string[] {
  return frameEffectEntries(frame).map((e) => e.id);
}

/**
 * Freeze a **frame trait's or core system's own RAW frequency cap** (the Everest's "Initiative":
 * `active_effects: [{ frequency: "1/scene", ... }]`; a `core_system`'s own `frequency` and its
 * `active_actions[]`' `trigger`, e.g. the granted Boost's `1/turn` cadence; the core activation
 * itself always freezes the {@link CORE_POWER_LIMITED} pool instead) onto
 * the effect-class {@link "@lancer-vtt/domain".Affordance} records — the cap half that
 * {@link buildAffordances} joins onto {@link frameEffectAffordanceSources}, the direct sibling of
 * what {@link freezeActionCaps} produces for compendium-item-granted actions — a frame-trait/core-system-granted `request_activate` (e.g.
 * `everest_initiative`, `everest_hyperspec_fuel_injector`, `everest_hyperspec_fuel_injector_boost`)
 * rides the EXACT SAME generic gate (`checkGrantedActionLegality`) and consume
 * (`spend_action_use`) that gate already enforces for any OTHER capped granted action, keyed by
 * the entry's own synthesized id (see {@link frameEffectEntries}) — no new legality/consume code
 * needed, only this freeze. An entry with no (or unrecognized) frequency contributes no cap
 * (unrestricted, e.g. every passive stat trait).
 */
export function freezeFrameTraitCaps(frame: Record<string, unknown> | undefined): FrozenEconomyFacts {
  const out: FrozenEconomyFacts = { caps: {}, limited: {} };
  const coreId = coreSystemActivationId(frame);
  for (const entry of frameEffectEntries(frame)) {
    if (entry.id === coreId) {
      out.limited[entry.id] = CORE_POWER_LIMITED;
      continue;
    }
    const cap = parseActionFrequency(entry.frequency);
    if (cap) out.caps[entry.id] = cap;
  }
  return out;
}

/**
 * The Limited pool every frame's **core system activation** freezes, and no rate, whatever
 * `lancer-data` prints as its `frequency` (only the Everest prints one, `"1/scene"`; the rest print
 * none, and a pool of 1 already holds it to once a scene). A mech has 1 Core Power, spent by the
 * activation and regained only on a Full Repair or as a GM boon (Core p.32, p.263: "able to use
 * their CORE SYSTEM only once between FULL REPAIRS"), so the spend must outlive the encounter. A
 * `limited` pool routes it through the Limited-system persistence the commit
 * extractor (`limitedSystemUsedCounts`) and `spawnMech`'s re-seed already carry; Full Repair clears
 * it with the rest of the printed record. The `CORE_POWER_POOL` slot keeps it out of the 2-Repair
 * `track: "limited"` replenish, which restores Limited systems but not CP. The core system's granted
 * `active_actions[]` (the Everest's 1/turn Boost) keep their own printed cadence.
 */
const CORE_POWER_LIMITED: AffordanceLimited = { max: 1, pool: CORE_POWER_POOL };

/** The synthesized id of `frame`'s core system activation ({@link coreSystemEffectEntries}' first entry), or `undefined` for a frame with no named core system. */
function coreSystemActivationId(frame: Record<string, unknown> | undefined): string | undefined {
  const frameName = frame && typeof frame["name"] === "string" ? frame["name"] : undefined;
  const cs = frame?.["core_system"];
  if (!frameName || !cs || typeof cs !== "object") return undefined;
  const name = (cs as RawCoreSystem).name;
  return typeof name === "string" && name.trim() ? `${slugifySnake(frameName)}_${slugifySnake(name)}` : undefined;
}

/**
 * The **effect-class affordance sources** a frame contributes — the structured half of
 * {@link freezeFrameTraitCaps}, minted as `{kind: "effect", entityId}` with **no index**
 * (`decisions/0176`).
 *
 * **No index is the design, not an omission.** For this class the bare id is the whole and
 * correct address: `everest_hyperspec_fuel_injector` (Power Up) authors TWO `kind:"action"`
 * clauses and one bare activation fires **both** — that is `gatherActions`' documented bare path
 * — and its `1/scene` cap is frozen once, for the entry as a whole, at
 * the record keyed bare `"everest_hyperspec_fuel_injector"`. An entry-level fire is the only thing
 * that could consume an entry-level cap correctly, so giving this class per-clause records would
 * break Power Up rather than describe it. Compare the granted class, where one entry mints one
 * record per position in `decisions/0123`'s unified affordance space.
 *
 * **Scoped to the entries that carry a frozen rate or pool**, i.e. exactly the key set
 * {@link freezeFrameTraitCaps} produces — a passive entry that freezes no cap freezes no record
 * either, which is what `affordanceTable.pin.test.ts` asserts positionally. The frame's *passive* traits (Replaceable
 * Parts and friends) are deliberately absent: they gather no action clause and carry no economy
 * fact, so a record for them would assert an affordance that does not exist.
 *
 * **This is not the complete effect-class enumeration, and Phase 1 does not try to be.** Which
 * core bonuses / talent ranks / NPC-feature effect ids actually gather an `action` clause is an
 * OVERLAY question (`carriedPilotFrameActionIds` answers it at call time, with an overlay in
 * hand), and spawn may be handed no overlay at all. The frozen table carries the entries whose
 * economy facts were already frozen; the door parser resolves the rest by exact match, per
 * `decisions/0176` ruling 3.
 */
export function frameEffectAffordanceSources(frame: Record<string, unknown> | undefined): AffordanceSource[] {
  const { caps, limited } = freezeFrameTraitCaps(frame);
  return frameEffectEntries(frame)
    .filter((entry) => caps[entry.id] !== undefined || limited[entry.id] !== undefined)
    .map((entry) => ({ kind: "effect", entityId: entry.id }));
}
